import { makeAutoObservable } from "mobx"
import ModalState from "reusable/mobx-helpers/ModalState"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { ThemeStore } from "theme/ThemeStore"
import TicTacToeStore from "./TicTacToeStore"
import WebSocketStore from "websockets/WebSocketStore"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { WebSocketResponseDto } from "@gap/backend/websockets/models/WebSocketResponseDto"
import Shrek from '/images/shrek-back.webp'
import { snackbarSuccess } from "reusable/mui/snackbar/snackbar"
import Debug from "reusable/utils/debug"
import useMatrix from "./Matrix"
import { MemeSound } from "./Sound"

interface GooseBotStoreProps {
   themeStore: ThemeStore
   webSocketStore: WebSocketStore
   orgGuardStore: OrgGuardStore
   matrix: ReturnType<typeof useMatrix>
}

export default class GooseBotStore {
   modalState = new ModalState()
   messages: { message: string, type: 'user' | 'goose' }[] = []
   sp
   fields = {
      inputField: '',
   }
   ticTacToeStore = new TicTacToeStore()

   constructor(sp: GooseBotStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
      if (sp.orgGuardStore.userData.email?.includes('gaphq.com')) {
         this.sp.webSocketStore.addSubscriptions({
            orgId: sp.orgGuardStore.userData.userOrg.orgId,
            userId: sp.orgGuardStore.userData.id,
            subscriptions: [
               {
                  event: 'goose-command',
                  entityId: sp.orgGuardStore.userData.id,
                  callback: async (response: WebSocketResponseDto) => {
                     const message = JSON.parse(response.message)
                     this.command(message.command)
                     return
                  }
               }
            ]
         })
      }
   }

   get formHandler() {
      return formHandler(this.fields, {
         inputField: { type: 'string' }
      } satisfies FormHandlerConfig)
   }

   onKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
         const input = this.fields.inputField.trim()
         if (!input) return
         this.messages.push({ message: input, type: 'user' })
         await new Promise(f => setTimeout(f, 300))
         const output = this.command(input)
         if (output) {
            this.messages.push({ message: output, type: 'goose' })
         }
         this.fields.inputField = ''
      }
   }

   command = (fullCommand: string): string | null => {
      const [command, ...args] = fullCommand.split(' ')

      if (this.ticTacToeStore.gameActive && /^\d$/.test(command)) {
         const index = parseInt(command) - 1
         const result = this.ticTacToeStore.makeMove(index)
         return result ?? `${this.ticTacToeStore.printBoard()}`
      }

      switch (command.toLowerCase()) {
         case 'help':
            return 'Doing the best I can here. Type "commands" for available options.'
         case 'commands':
            return 'Available commands: help, tictactoe, shrek, clear, debug'
         case 'shrek':
            if (this.sp.themeStore.backgroundImageUrl) {
               this.sp.themeStore.setBackgroundImage()
               return 'Reverted Shrek'
            } else {
               this.sp.themeStore.setBackgroundImage(`url(${Shrek})`)
               return 'Rolled out the Shrek'
            }
         case 'background':
            this.sp.themeStore.setBackgroundImage(`url(${args[0]})`)
            return 'Alright then...'
         case 'sound':
            MemeSound({ url: args[0] })
            return "Sound injected"
         case 'clear':
            this.messages = []
            return null
         case 'goose':
         case 'honk':
            return '🪿🪿 Honk 🪿🪿'
         case 'message':
            snackbarSuccess(args.join(' '))
            return 'Message sent'
         case 'debug':
            return Debug.toggle() ? 'Debug mode enabled' : 'Debug mode disabled'
         case 'matrix':
            return this.sp.matrix.toggle() ? 'Matrix mode enabled' : 'Matrix mode disabled'
         case 'tic':
         case 'tictactoe':
            this.ticTacToeStore.startGame()
            return 'Tic Tac Toe started!\n' + this.ticTacToeStore.printBoard() + '\nType a number (1-9) to make a move.'
         default:
            return 'Can\'t help you with that mate'
      }
   }
}
